"use client";

import { cn } from "@/lib/utils/cn";

type Sector = { sector: string; percentage: number };
type Holding = { name: string; percentage: number };

type FundHoldingsChartProps = {
  sectors: Sector[];
  topHoldings: Holding[];
};

const BAR_COLORS = [
  "bg-blue",
  "bg-cgreen",
  "bg-gold",
  "bg-corange",
  "bg-cred",
  "bg-blue/70",
  "bg-cgreen/70",
  "bg-gold/70",
  "bg-corange/70",
  "bg-cred/70",
];

export function FundHoldingsChart({
  sectors,
  topHoldings,
}: FundHoldingsChartProps) {
  const maxSectorPct = Math.max(...sectors.map((s) => s.percentage), 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Sector Allocation — horizontal bar chart */}
      <div>
        <h3 className="text-foreground text-sm font-serif mb-4">
          Sector Allocation
        </h3>
        <div className="space-y-2.5">
          {sectors.map((s, i) => (
            <div key={s.sector}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-muted-foreground text-xs truncate mr-2">
                  {s.sector}
                </span>
                <span className="text-foreground text-xs font-semibold font-tabular shrink-0">
                  {s.percentage.toFixed(1)}%
                </span>
              </div>
              <div className="bg-navy-mid rounded h-2 overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded transition-all duration-700",
                    BAR_COLORS[i % BAR_COLORS.length]
                  )}
                  style={{
                    width: `${(s.percentage / maxSectorPct) * 100}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Holdings — table list */}
      <div>
        <h3 className="text-foreground text-sm font-serif mb-4">
          Top Holdings
        </h3>
        <div className="space-y-0">
          {topHoldings.map((h, i) => (
            <div
              key={h.name}
              className="flex items-center justify-between py-2 border-b border-surface-light/15 last:border-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-dim text-[10px] font-tabular w-4 shrink-0">
                  {i + 1}.
                </span>
                <span className="text-foreground text-sm truncate">
                  {h.name}
                </span>
              </div>
              <span className="text-muted-foreground text-sm font-semibold font-tabular shrink-0 ml-2">
                {h.percentage.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
